/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitShiftSummaryProps {
  id: string;
  name: string;
  startTimeInMinutes: number;
  endTimeInMinutes: number;
}

export class UnitShiftSummaryRecord extends Record<UnitShiftSummaryProps>({
  id: null as any,
  name: null as any,
  startTimeInMinutes: null as any,
  endTimeInMinutes: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    startTimeInMinutes: number;
    endTimeInMinutes: number;
  }): UnitShiftSummaryRecord {
    if (json == null) {
      throw new Error('UnitShiftSummaryRecord.fromJS: parameter cannot be null');
    }
    return new UnitShiftSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitShiftSummaryJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'UnitShiftSummaryJson.name'),
      startTimeInMinutes: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'startTimeInMinutes', 0),
        'UnitShiftSummaryJson.startTimeInMinutes',
      ),
      endTimeInMinutes: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'endTimeInMinutes', 0),
        'UnitShiftSummaryJson.endTimeInMinutes',
      ),
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    startTimeInMinutes: number;
    endTimeInMinutes: number;
  }): [UnitShiftSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitShiftSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitShiftSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitShiftSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'UnitShiftSummaryJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      startTimeInMinutes: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'startTimeInMinutes', 0),
            'UnitShiftSummaryJson.startTimeInMinutes',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endTimeInMinutes: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'endTimeInMinutes', 0),
            'UnitShiftSummaryJson.endTimeInMinutes',
          ),
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: UnitShiftSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitShiftSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitShiftSummaryViewModelBase implements UnitShiftSummaryProps {
  protected readonly _props: UnitShiftSummaryProps;
  private _json?: UnitShiftSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get startTimeInMinutes(): number {
    return this._props.startTimeInMinutes;
  }
  get endTimeInMinutes(): number {
    return this._props.endTimeInMinutes;
  }

  constructor(props: UnitShiftSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        startTimeInMinutes: this._props.startTimeInMinutes,
        endTimeInMinutes: this._props.endTimeInMinutes,
      };
    }
    return this._json;
  }
}
